import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Pencil, Copy, Trash2, Eye, Globe, Undo2, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, btnSecondary, StatusBadge } from "@/components/admin/ui";

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", slug: "", seo_title: "", seo_description: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Page name required"); return; }
    setBusy(true);
    try { const p = await api.cmsCreate(f); toast.success("Page created"); onCreated(p); }
    catch { toast.error("Could not create page"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#F9F8F6] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="cms-create-modal">
        <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-2xl text-[#1F2A22]">Create New Page</h2><button onClick={onClose}><X size={20} /></button></div>
        {[["name", "Page Name"], ["slug", "Page URL (slug)"], ["seo_title", "SEO Title"], ["seo_description", "SEO Description"]].map(([k, label]) => (
          <label key={k} className="block mb-3">
            <span className="text-xs text-[#5C6B61]">{label}</span>
            <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="w-full border border-[#1F2A22]/20 px-3 py-2 mt-1 bg-white" data-testid={`cms-new-${k}`} />
          </label>
        ))}
        <p className="text-xs text-[#5C6B61] mb-4">URL will be <code>/p/{f.slug ? f.slug.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "your-page"}</code></p>
        <div className="flex gap-3"><button onClick={save} disabled={busy} className={btnPrimary} data-testid="cms-new-save">{busy ? <Loader2 size={15} className="animate-spin" /> : "Create Page"}</button><button onClick={onClose} className={btnSecondary}>Cancel</button></div>
      </div>
    </div>
  );
}

export default function AdminPages() {
  const [rows, setRows] = useState(null);
  const [trash, setTrash] = useState(false);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => { setRows(null); api.cmsPages(trash).then(setRows).catch(() => setRows([])); }, [trash]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch { toast.error("Action failed"); } };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className={OVERLINE}>Website</p><h1 className={H1}>Pages</h1></div>
        <button onClick={() => setCreating(true)} className={btnPrimary} data-testid="cms-create-btn"><Plus size={16} /> Create New Page</button>
      </div>

      <div className="flex gap-2 mt-6 mb-4">
        <button onClick={() => setTrash(false)} className={!trash ? btnPrimary : btnSecondary} data-testid="cms-tab-active">Active</button>
        <button onClick={() => setTrash(true)} className={trash ? btnPrimary : btnSecondary} data-testid="cms-tab-trash">Trash</button>
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-[#5C6B61] py-10">{trash ? "Trash is empty." : "No pages yet. Create your first page."}</p>
      ) : (
        <div className="border border-[#1F2A22]/12 divide-y divide-[#1F2A22]/10 bg-white" data-testid="cms-pages-list">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center gap-4 px-4 py-3 flex-wrap" data-testid={`cms-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1F2A22] truncate">{p.name}</p>
                <p className="text-xs text-[#5C6B61]">/p/{p.slug} · updated by {p.updated_by || "—"}</p>
              </div>
              {!trash && <StatusBadge status={p.status} />}
              {!trash ? (
                <div className="flex items-center gap-1.5">
                  <Link to={`/admin/pages/${p.id}`} className="p-2 hover:bg-[#E5E2DC]" title="Edit" data-testid={`cms-edit-${p.slug}`}><Pencil size={16} /></Link>
                  {p.status === "published" && <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-[#E5E2DC]" title="View live"><Eye size={16} /></a>}
                  {p.status === "published"
                    ? <button onClick={() => act(() => api.cmsUnpublish(p.id), "Unpublished")} className="p-2 hover:bg-[#E5E2DC]" title="Unpublish"><Globe size={16} className="text-[#5C6B61]" /></button>
                    : <button onClick={() => act(() => api.cmsPublish(p.id), "Published")} className="p-2 hover:bg-[#E5E2DC]" title="Publish" data-testid={`cms-publish-${p.slug}`}><Globe size={16} className="text-[#FF6A00]" /></button>}
                  <button onClick={() => act(() => api.cmsDuplicate(p.id), "Duplicated")} className="p-2 hover:bg-[#E5E2DC]" title="Duplicate"><Copy size={16} /></button>
                  <button onClick={() => act(() => api.cmsTrash(p.id), "Moved to trash")} className="p-2 hover:bg-[#E5E2DC]" title="Trash" data-testid={`cms-trash-${p.slug}`}><Trash2 size={16} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => act(() => api.cmsRestore(p.id), "Restored")} className="p-2 hover:bg-[#E5E2DC]" title="Restore" data-testid={`cms-restore-${p.slug}`}><Undo2 size={16} /></button>
                  <button onClick={() => { if (window.confirm("Permanently delete this page? This cannot be undone.")) act(() => api.cmsDeleteForever(p.id), "Deleted permanently"); }} className="p-2 hover:bg-[#E5E2DC] text-red-600" title="Delete forever"><RotateCcw size={16} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(p) => { setCreating(false); window.location.href = `/admin/pages/${p.id}`; }} />}
    </div>
  );
}
